import Spinner from "./classes/Spinner.js";
import Template from "./assets/PhotoMotionSpinner.html?raw";
import style from "./assets/main.scss?inline";

const ALLOWED_ORIGINS = [
  "http://127.0.0.1",
  "http://localhost",
  "https://spinner.photo-motion.com",
  "https://spinner.photo-motion.io",
  "https://spinner.photo-motion.dev",
  "https://storage.googleapis.com/photomotion-spinner-data-dev",
  "https://storage.googleapis.com/photomotion-spinner-data-prod",
  "https://storage.googleapis.com/photomotion-spinner-data-acc",
];

const ARTICLE_HASH = "article-hash";
const ARTICLE_DATA = "article-data";
const ARTICLE_URL = "article-url";
const CAN_SCROLL = "can-scroll";
const CAN_FULLSCREEN = "can-fullscreen";
const DEFAULT_LAYER = "default-layer";
const VIEWER_MODE = "viewer-mode";
const MAX_ZOOM = "max-zoom";
const SPINNING_SPEED = "spinning-speed";
const CLEAN_UI = "clean-ui";
const BUTTONS_PLACEMENT = "buttons-placement";
const HIDE_GALLERY = "hide-gallery";
const HIDE_DOORS = "hide-doors";
const HIDE_LIGHTS = "hide-lights";
const HIDE_SHOWCASE = "hide-showcase";
const HIDE_INTERIOR = "hide-interior";
const HIDE_ZOOM = "hide-zoom";
const HIDE_HOTSPOTS = "hide-hotspots";
const INSPECTOR_MODE = "inspector-mode";

let replacements = {
  VITE_BRAND_URL: import.meta.env.VITE_BRAND_URL,
};

class PhotoMotionSpinner extends HTMLElement {
  /**
   * @type {Spinner|null}
   */
  #spinner = null;
  #connected;
  #hasBeenConnected;
  #hashThrottle;
  #uiConfigThrottle;
  #root;

  constructor() {
    super();
    this.#connected = new Promise((resolve) => {
      this.#hasBeenConnected = resolve;
    });
  }

  static get observedAttributes() {
    return [
      ARTICLE_HASH,
      ARTICLE_DATA,
      ARTICLE_URL,
      CAN_SCROLL,
      CAN_FULLSCREEN,
      DEFAULT_LAYER,
      VIEWER_MODE,
      MAX_ZOOM,
      SPINNING_SPEED,
      CLEAN_UI,
      BUTTONS_PLACEMENT,
      HIDE_GALLERY,
      HIDE_DOORS,
      HIDE_LIGHTS,
      HIDE_SHOWCASE,
      HIDE_INTERIOR,
      HIDE_ZOOM,
      HIDE_HOTSPOTS,
      INSPECTOR_MODE,
    ];
  }

  setAttribute(qualifiedName, value) {
    const oldToNewKey = {
      hash: ARTICLE_HASH,
      buttons: BUTTONS_PLACEMENT,
      scroll: CAN_SCROLL,
      fullscreen: CAN_FULLSCREEN,
      mode: VIEWER_MODE,
    };
    super.setAttribute(oldToNewKey[qualifiedName] || qualifiedName, value);
  }

  get inspectorMode() {
    return this.hasAttribute(INSPECTOR_MODE);
  }

  set inspectorMode(val) {
    if (val) {
      this.setAttribute(INSPECTOR_MODE, "");
    } else {
      this.removeAttribute(INSPECTOR_MODE);
    }

    if (this.#spinner) this.#spinner.inspectorMode = val;
  }

  get notFound() {
    return this.#spinner.notFound;
  }

  // Add not-found attribute to the web component element
  set notFound(val) {
    if (val) {
      this.setAttribute("not-found", "");
    } else {
      this.removeAttribute("not-found");
    }

    if (this.#spinner) this.#spinner.notFound = val;
  }

  get cleanUi() {
    return this.hasAttribute("clean-ui");
  }

  set cleanUi(val) {
    if (val) {
      this.setAttribute("clean-ui", "");
    } else {
      this.removeAttribute("clean-ui");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(val) {
    if (val) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  connectedCallback() {
    const mode = ALLOWED_ORIGINS.some((origin) =>
      window.location.origin.startsWith(origin),
    )
      ? "open"
      : "closed";

    this.#root = this.shadowRoot ?? this.attachShadow({ mode });
    const sheet = new CSSStyleSheet();

    sheet.replaceSync(
      ":host { display: block; position: relative; } :host([disabled]) { pointer-events:none } " +
        style,
    );
    this.#root.adoptedStyleSheets = [sheet];

    let populatedTemplate = Template;

    for (let [key, value] of Object.entries(replacements)) {
      populatedTemplate = populatedTemplate.replaceAll(`%${key}%`, value);
    }

    this.#root.innerHTML = populatedTemplate;

    this.#hasBeenConnected();
  }

  disconnectedCallback() {
    this.#connected = new Promise((resolve) => {
      this.#hasBeenConnected = resolve;
    });
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    if (attrName === "article-hash") {
      this.articleHashChanged(newVal, oldVal);
    } else if (attrName === "article-data") {
      this.articleDataChanged(newVal, oldVal);
    } else if (attrName === "article-url") {
      this.articleUrlChanged(newVal, oldVal);
    } else {
      this.updateUiConfig();
    }
  }

  /**
   *
   * @returns {UIConfig}
   */
  getUiConfig() {
    return {
      canScroll: this.getAttribute(CAN_SCROLL),
      canFullscreen: this.getAttribute(CAN_FULLSCREEN),
      defaultLayer: this.getAttribute(DEFAULT_LAYER),
      viewerMode: this.getAttribute(VIEWER_MODE),
      maxZoom: parseFloat(this.getAttribute(MAX_ZOOM) ?? 0),
      spinningSpeed: parseFloat(this.getAttribute(SPINNING_SPEED) ?? 0),
      cleanUi: this.hasAttribute(CLEAN_UI),
      buttonsPlacement: this.getAttribute(BUTTONS_PLACEMENT),
      hideGallery: this.hasAttribute(HIDE_GALLERY),
      hideDoors: this.hasAttribute(HIDE_DOORS),
      hideLights: this.hasAttribute(HIDE_LIGHTS),
      hideShowcase: this.hasAttribute(HIDE_SHOWCASE),
      hideInterior: this.hasAttribute(HIDE_INTERIOR),
      hideZoom: this.hasAttribute(HIDE_ZOOM),
      hideHotspots: this.hasAttribute(HIDE_HOTSPOTS),
    };
  }

  articleHashChanged(value, oldValue) {
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          this.#spinner.setHash(value, this.getUiConfig()).catch((e) => {
            console.error(e);
            this.notFound = true;
          });
        });
      })
      .catch(console.error);
  }

  articleDataChanged(value, oldValue) {
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          const previewObject = JSON.parse(value);

          /* eslint-disable @typescript-eslint/naming-convention */
          const data = {
            default_layer: previewObject.default_layer,
            images_base_url: previewObject.images_base_url,
            video_base_url: previewObject.video_base_url,
            spinner_hash: previewObject.spinner_hash,
            interior_image: previewObject.interior_image,
            interior_image_url: previewObject.interior_image_url,
            layers: previewObject.layers ?? {},
            show_branding: previewObject.show_branding ?? true,
            viewer_mode: previewObject.viewer_mode,
            placeholder: previewObject.placeholder,
            bounding_box: previewObject.bounding_box,
          };
          /* eslint-enable @typescript-eslint/naming-convention */

          this.#spinner.setData(data);
          this.#spinner.applyUiConfig(this.getUiConfig());
        });
      })
      .catch(console.error);
  }

  articleUrlChanged(value, oldValue) {
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          if (
            !value ||
            !ALLOWED_ORIGINS.some((origin) => value.startsWith(origin))
          ) {
            this.notFound = true;
            return;
          }

          fetch(value)
            .then((response) => response.json())
            .then((data) => {
              this.notFound = false;
              this.#spinner.setData(data);
              this.#spinner.applyUiConfig(this.getUiConfig());
            })
            .catch((e) => {
              console.error(e);
              this.notFound = true;
            });
        });
      })
      .catch(console.error);
  }

  updateUiConfig() {
    if (!this.#spinner) {
      return;
    }

    this.#connected
      .then(() => {
        cancelAnimationFrame(this.#uiConfigThrottle);
        this.#uiConfigThrottle = requestAnimationFrame(() => {
          this.#spinner.inspectorMode = this.inspectorMode;
          this.#spinner.notFound = this.notFound;
          this.#spinner.applyUiConfig(this.getUiConfig());
        });
      })
      .catch(console.error);
  }
}

customElements.define("photo-motion-spinner", PhotoMotionSpinner);
